import React from 'react';
import { ChevronDown, CircuitBoard, FolderOpen, Thermometer } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { isElectricalEnabled } from '../../lib/sim/electrical/electricalFeatureFlag';
import { isEoEngineActive } from '../../lib/sim/eo/eoEngineFlag';

export interface SimulationWorkspaceNavigationProps {
  activeDomain: 'electrical' | 'plant';
  /** Lets the application preserve its editor view and project context. */
  onPlantClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

/** Inline header navigation: changing workspace never changes a model's solver. */
export function SimulationWorkspaceNavigation({
  activeDomain,
  onPlantClick,
}: SimulationWorkspaceNavigationProps): React.ReactElement {
  const linkClass = (active: boolean): string =>
    `inline-flex min-h-10 shrink-0 items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
      active
        ? 'border-primary bg-primary/5 text-primary'
        : 'border-transparent text-text-secondary hover:bg-state-hover hover:text-text-primary'
    }`;

  const handlePlantClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    if (
      !event.defaultPrevented && event.button === 0 &&
      !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
    ) {
      onPlantClick?.(event);
    }
  };
  const activeLabel = activeDomain === 'electrical' ? 'Electrical' : 'Plant & Cooling';
  const ActiveIcon = activeDomain === 'electrical' ? CircuitBoard : Thermometer;

  return (
    <>
    <div className="hidden shrink-0 items-center gap-3 min-[1400px]:inline-flex">
      <nav className="flex shrink-0 items-center gap-1" aria-label="Simulation workspaces">
        {isElectricalEnabled() && <Link
          to="/editor/electrical"
          data-testid="electrical-workspace-link"
          aria-current={activeDomain === 'electrical' ? 'page' : undefined}
          className={linkClass(activeDomain === 'electrical')}
        >
          <CircuitBoard className="h-4 w-4" aria-hidden="true" />
          Electrical
        </Link>}
        {isEoEngineActive() && <Link
          to="/editor"
          aria-current={activeDomain === 'plant' ? 'page' : undefined}
          onClick={handlePlantClick}
          className={linkClass(activeDomain === 'plant')}
        >
          <Thermometer className="h-4 w-4" aria-hidden="true" />
          Plant &amp; Cooling
        </Link>}
      </nav>
      <Link to="/projects" className="whitespace-nowrap rounded-sm border border-border-subtle px-3 py-2 text-xs font-semibold text-primary hover:bg-state-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">Shared projects</Link>
    </div>
    <div className="shrink-0 min-[1400px]:hidden">
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={`Simulation workspaces: ${activeLabel}`}
          className="inline-flex h-9 items-center gap-1 rounded-md border border-border-subtle bg-primary/5 px-2 text-xs font-semibold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <ActiveIcon className="h-4 w-4" aria-hidden="true" />
          <span className="sm:hidden">{activeDomain === 'plant' ? 'Plant' : 'Electrical'}</span>
          <span className="hidden sm:inline">{activeLabel}</span>
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          {isElectricalEnabled() && <DropdownMenuItem nativeButton={false} render={<Link to="/editor/electrical" />} aria-current={activeDomain === 'electrical' ? 'page' : undefined}>
            <CircuitBoard className="h-4 w-4" aria-hidden="true" />Electrical
          </DropdownMenuItem>}
          {isEoEngineActive() && <DropdownMenuItem nativeButton={false} render={<Link to="/editor" onClick={handlePlantClick} />} aria-current={activeDomain === 'plant' ? 'page' : undefined}>
            <Thermometer className="h-4 w-4" aria-hidden="true" />Plant &amp; Cooling
          </DropdownMenuItem>}
          <DropdownMenuItem nativeButton={false} render={<Link to="/projects" />}>
            <FolderOpen className="h-4 w-4" aria-hidden="true" />Shared projects
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
    </>
  );
}

export default SimulationWorkspaceNavigation;
